import { MutableRefObject, RefObject, useLayoutEffect, useRef } from 'react';
import usePrefersReducedMotion from '@app/hooks/usePrefersReducedMotion';
import { measureWidgetRects, playFlip, WidgetRect } from './layoutFlip';

/**
 * Size-aware FLIP for Home widget slots.
 * Always keeps `positionsRef` fresh (including idle/settle) so hide/show and
 * future reorder can read First rects before the next layout commit.
 */
const useFlipLayoutAnimation = (
  containerRef: RefObject<HTMLElement | null>,
  animationKey: string,
  isAnimating: boolean
): MutableRefObject<Map<string, WidgetRect>> => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const positionsRef = useRef<Map<string, WidgetRect>>(new Map());
  const isFirstLayoutRef = useRef(true);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const nextPositions = measureWidgetRects(container);

    if (prefersReducedMotion || !isAnimating || isFirstLayoutRef.current) {
      positionsRef.current = nextPositions;
      isFirstLayoutRef.current = false;
      return;
    }

    const previousPositions = positionsRef.current;
    const elements = Array.from(
      container.querySelectorAll<HTMLElement>(
        '[data-layout-animated][data-widget-id]:not([data-layout-exiting="true"])'
      )
    );

    playFlip(elements, previousPositions, nextPositions);
    positionsRef.current = nextPositions;
  }, [animationKey, containerRef, prefersReducedMotion, isAnimating]);

  return positionsRef;
};

export default useFlipLayoutAnimation;
